import { useEffect, useRef } from 'react';
import type { Node } from 'reactflow';
import { resolveExchangeCluster } from '../utils/resolveExchangeCluster';

const QUIESCENCE_MS = 500;          // D3 — reframe after turns STOP landing
const DIRECTOR_MAX_ZOOM = 1.0;      // D8 — frame-with-context, no punch-in

/** TH-3b — while `follow` (and not `paused`), reframe on quiescence to the latest
 * landed exchange cluster + publish its turnKeys as the spotlight set. rev2: only
 * ever reframe a NEW anchor — a grace-resume (paused true→false, same anchor) must
 * NOT yank the camera back. */
export function useDirectorRun(opts: {
  follow: boolean; paused: boolean;
  nodes: Node[]; latestAnchor: string | null;   // the newest turn's anchor turnKey
  fitWithRetry: (o: { nodes?: { id: string }[]; padding?: number; duration?: number; maxZoom?: number }) => void;
  setSpotlightTurnKeys: (k: ReadonlySet<string> | null) => void;
}) {
  const { follow, paused, nodes, latestAnchor, fitWithRetry, setSpotlightTurnKeys } = opts;
  const timer = useRef<number | null>(null);
  const nodesRef = useRef(nodes); nodesRef.current = nodes;
  const lastFramedAnchor = useRef<string | null>(null);   // rev2: reframe only a NEW anchor

  useEffect(() => {
    if (!follow) { lastFramedAnchor.current = null; return; }   // rev2: re-enable → re-frame fresh
    if (paused || !latestAnchor) return;
    if (latestAnchor === lastFramedAnchor.current) return;      // rev2: grace-resume must not yank
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const { nodeIds, turnKeys } = resolveExchangeCluster(nodesRef.current, latestAnchor);
      if (nodeIds.length === 0) return;               // D8 — nothing to frame
      lastFramedAnchor.current = latestAnchor;        // rev2: mark framed (late same-anchor nodes won't re-yank)
      setSpotlightTurnKeys(turnKeys);
      fitWithRetry({ nodes: nodeIds.map((id) => ({ id })), maxZoom: DIRECTOR_MAX_ZOOM, padding: 0.25, duration: 600 });
    }, QUIESCENCE_MS);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
    // pre-fire re-arm on nodes.length is the quiescence reset; post-fire same-anchor is guarded above
  }, [follow, paused, latestAnchor, nodes.length, fitWithRetry, setSpotlightTurnKeys]);
}
